import React from 'react';
import Icon from '../../../components/AppIcon';

const ImpactedSystems = ({ systems }) => {
  const getCriticalityColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case 'critical': return 'bg-error text-error-foreground';
      case 'high': return 'bg-warning text-warning-foreground';
      case 'medium': return 'bg-accent text-accent-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getHealthColor = (health) => {
    switch (health?.toLowerCase()) {
      case 'healthy': return 'text-success';
      case 'degraded': return 'text-warning';
      case 'down': return 'text-error';
      case 'unknown': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getHealthIcon = (health) => {
    switch (health?.toLowerCase()) {
      case 'healthy': return 'CheckCircle';
      case 'degraded': return 'AlertTriangle';
      case 'down': return 'XCircle';
      case 'unknown': return 'HelpCircle';
      default: return 'HelpCircle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
        <Icon name="Server" size={20} />
        Impacted Systems ({systems?.length})
      </h2>
      <div className="space-y-4">
        {systems?.map((system) => (
          <div key={system?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-foreground">{system?.name}</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCriticalityColor(system?.criticality)}`}>
                    {system?.criticality}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{system?.description}</p>
              </div>
              
              <div className="flex items-center gap-1 ml-4">
                <Icon 
                  name={getHealthIcon(system?.health)} 
                  size={16} 
                  className={getHealthColor(system?.health)} 
                />
                <span className={`text-sm font-medium ${getHealthColor(system?.health)}`}>
                  {system?.health}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2 text-foreground">{system?.type}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Environment:</span>
                <span className="ml-2 text-foreground">{system?.environment}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Owner:</span>
                <span className="ml-2 text-foreground">{system?.owner}</span>
              </div>
            </div>

            {/* Dependencies */}
            {system?.dependencies && system?.dependencies?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <h4 className="text-sm font-medium text-foreground mb-2">Dependencies</h4>
                <div className="flex flex-wrap gap-2">
                  {system?.dependencies?.map((dep, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">
                      {dep}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Impact Details */}
            {system?.impactDetails && (
              <div className="mt-3 pt-3 border-t border-border">
                <h4 className="text-sm font-medium text-foreground mb-1">Impact Details</h4>
                <p className="text-sm text-muted-foreground">{system?.impactDetails}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* System Health Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-medium text-foreground mb-3">System Health Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {['healthy', 'degraded', 'down', 'unknown']?.map((status) => {
            const count = systems?.filter(s => s?.health?.toLowerCase() === status)?.length;
            return (
              <div key={status} className="text-center">
                <div className={`text-2xl font-bold ${getHealthColor(status)}`}>
                  {count}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {status}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImpactedSystems;